import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from 'lodash';
import cx from 'classnames';

import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { processHTMLToWidget } from "../../../../actions/editor";
import { FailureToast } from "components/Toast";
import StoryboardContext from 'components/Storyboard/context';
import { STORYBOARD_TYPES } from "constants/storyboardType";

import styles from './importHtml.module.scss';

//Exporting a doc from Google Docs/Word wraps the real markup as escaped text —
//one <p> per line, with data-widget attributes sitting inside &lt;…&gt; entities.
//Read straight, that document has no widgets at all, so recover the source from
//the block text when the file turns out to be one of those exports.
export const resolveHtml = (raw) => {
  const text = _.trim(raw);

  if (!text) {
    return text;
  }

  const parsed = new DOMParser().parseFromString(text, 'text/html');

  //Real markup already — nothing to unwrap.
  if (parsed.querySelector('[data-widget]')) {
    return text;
  }

  const recovered = Array.from(_.get(parsed, 'body.children') || [])
    .map((element) => element.textContent)
    .join('\n')
    //Google Docs indents with &nbsp;, which is not valid whitespace inside tags.
    .replace(/\u00A0/g, ' ')
    .trim();

  if (!recovered) {
    return text;
  }

  //Only swap when the unwrapped text really is the page, so anything else is
  //handed over exactly as it was read.
  const recoveredDocument = new DOMParser().parseFromString(recovered, 'text/html');

  return recoveredDocument.querySelector('[data-widget]') ? recovered : text;
}

//Mirrors what the extractor needs to produce anything: at least one
//[data-widget] element AND at least one page-level element for it to sit in
//(see extractor/mapping/pageMap.js and extractor/index.js). Without both, the
//extractor silently yields no widgets, so reject the file up front.
export const isImportableHtml = (html) => {
  const text = _.trim(html);

  if (!text) {
    return false;
  }

  const doc = new DOMParser().parseFromString(text, 'text/html');

  if (!doc.querySelector('[data-widget]')) {
    return false;
  }

  const pages = Array.from(doc.querySelectorAll('section, footer, header, main, [data-widget="section"]'))
    .filter((element) => element.tagName.toLowerCase() !== 'header' || element.getAttribute('data-widget') !== 'menu');

  return pages.length > 0;
}

const showFailure = (description) => {
  toast(
    <FailureToast heading="Import failed" description={description} />,
    {
      type: toast.TYPE.DEFAULT,
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 5000,
    }
  );
}

export const ImportHtmlButton = ({ editorSession, disabled, onImport }) => {
  const dispatch = useDispatch();

  const fileInputReference = useRef(null);

  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardItem = useSelector(state => _.get(state, 'storyboards.storyboard.item', null));
  const storyboard = storyboardContext || storyboardItem;

  const storyboardType = _.get(storyboard, 'type', '');
  const storyboardId = _.get(storyboard, 'id', '');

  const htmlInputEnabled = useSelector(state => _.get(state, 'workspaces.workspace.item.htmlInputEnabled', false));
  const entityType = useSelector(state => _.get(state, `${editorSession}.present.entityType`));

  const allowed = storyboardType === STORYBOARD_TYPES.LANDING_PAGE
    && htmlInputEnabled
    && entityType === 'PAGE';

  const handleImportClick = () => {
    fileInputReference.current?.click();
  }

  const handleFileChange = (e) => {
    const input = e.target;
    const file = _.get(input, 'files[0]');

    if (!file) {
      return;
    }

    //`accept` only filters the picker, it doesn't stop a user switching the
    //dialog to "All Files", so the extension still has to be checked here.
    if (!/\.html?$/i.test(file.name)) {
      showFailure('Please select an .html file');
      input.value = '';
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const html = resolveHtml(reader.result);

      if (!isImportableHtml(html)) {
        showFailure('This HTML file is not a valid Cleverstory page and cannot be imported');
        return;
      }

      dispatch(
        processHTMLToWidget(
          editorSession,
          [],
          html,
          storyboardId,
          []
        )
      );

      onImport?.();
    };

    reader.onerror = () => {
      showFailure(`Could not read ${file.name}`);
    };

    //Reset only once the read has finished, so picking the same file again
    //still fires `change` without clearing the input mid-read.
    reader.onloadend = () => {
      input.value = '';
    };

    reader.readAsText(file, 'UTF-8');
  };

  if (!allowed) {
    return null;
  }

  return (
    <>
      <Button
        variant="primary"
        className={styles.importHtmlButton}
        disabled={disabled}
        onClick={handleImportClick}
      >
        Upload HTML
      </Button>

      <input
        type="file"
        title=''
        name="Import HTML"
        accept=".html,.htm,text/html"
        multiple={false}
        className={styles.fileInput}
        ref={fileInputReference}
        onClick={(e) => e.stopPropagation()}
        onChange={handleFileChange}
      />
    </>
  );
}

export default ImportHtmlButton;
